import React from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../../stores';
import { Endpoints, Defaults } from '../../../../../../views/democracy/constants';
import { copyToClipboard, extractErrorMessage, buildSingleFileMultipart, validateMediaFile } from '../../../../../../views/democracy/utils';
import { CopyIcon } from '../../../../model/icons';
import AudioSlot from './AudioSlot';

// Карточка команды в настройках (.tc): имя/код + аудио пасс/ответ + ссылка на правку.
export default function TeamMgmtCard({ team, onEdit, onChanged, dragHandleProps }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();

    const handleCopy = async () => {
        const ok = await copyToClipboard(team.joinCode);
        enqueueSnackbar(ok ? 'Код скопирован' : 'Не удалось скопировать', { variant: ok ? 'success' : 'warning' });
    };

    const endpointOf = (slot) => (slot === 'passAudio' ? Endpoints.teams.passAudio(team.id) : Endpoints.teams.answerAudio(team.id));

    const uploadAudio = async (slot, file) => {
        const validationError = validateMediaFile(file);
        if (validationError) {
            enqueueSnackbar(validationError, { variant: 'error' });
            return;
        }
        show();
        try {
            await apiClient.put(endpointOf(slot), buildSingleFileMultipart(file));
            enqueueSnackbar('Звук сохранён', { variant: 'success' });
            onChanged();
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось сохранить звук'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    const clearAudio = async (slot) => {
        show();
        try {
            await apiClient.delete(endpointOf(slot));
            enqueueSnackbar('Звук сброшен', { variant: 'success' });
            onChanged();
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось сбросить звук'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    return (
        <div className="tc">
            <div className="th">
                <div className="nm">
                    {dragHandleProps && (
                        <span className="drag" title="Перетащить для смены порядка" {...dragHandleProps}>⠿</span>
                    )}
                    <span className="dot" style={{ background: team.color || Defaults.teamColor }} />
                    <span className="label">{team.name}</span>
                </div>
                <span className="num">№{team.orderId}</span>
            </div>

            <div className="joincode">
                <span className="k">код подключения</span>
                <span className="v">{team.joinCode || '—'}</span>
                <button type="button" className="cp" onClick={handleCopy} title="Копировать" disabled={!team.joinCode}><CopyIcon /></button>
            </div>

            <div className="kv"><span className="k">Старт. баланс</span><span className="v">{team.initialBalance ?? 0}</span></div>
            <div className="kv"><span className="k">Старт. рейтинг</span><span className="v">{team.initialRating ?? 0}</span></div>
            {onEdit && <button type="button" className="editlink" onClick={onEdit}>Редактировать</button>}

            <div className="sounds">
                <div className="media-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <AudioSlot caption="Пасс" url={team.passAudio?.mediaFileUrl || null}
                        onUpload={(f) => uploadAudio('passAudio', f)} onClear={() => clearAudio('passAudio')} />
                    <AudioSlot caption="Ответ" url={team.answerAudio?.mediaFileUrl || null}
                        onUpload={(f) => uploadAudio('answerAudio', f)} onClear={() => clearAudio('answerAudio')} />
                </div>
            </div>
        </div>
    );
}

TeamMgmtCard.propTypes = {
    team: PropTypes.object.isRequired,
    onEdit: PropTypes.func,
    onChanged: PropTypes.func.isRequired,
    dragHandleProps: PropTypes.object
};

TeamMgmtCard.defaultProps = { onEdit: null, dragHandleProps: null };
